import React, { useCallback, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useStrings } from '@/hooks/useStrings';
import { safeWrite } from '@/utils/supabaseWrite';
import { vibrateTap } from '@/utils/sound';
import { track } from '@/utils/analytics';

// PersonalizeStep — first onboarding screen. Captures life stage +
// last period start + top concerns so the rest of the app can
// personalize from minute one instead of waiting for the user to
// dig into Profile settings.
//
// All three questions are skippable. Defaults that result in the
// most permissive experience are: life_stage='unspecified',
// cycle_last_period_start=null (CycleArc shows the setup nudge),
// concerns=[] (no IntentPicker prefill).
//
// Saves are fire-and-forget per the patchProgress + safeWrite
// pattern. The "Continue" CTA always advances to the next onboarding
// step; the user is never blocked on a slow Supabase write.

const LIFE_STAGES = [
  { value: 'cycling',         labelKey: 'personalize_stage_cycling' },
  { value: 'perimenopause',   labelKey: 'personalize_stage_peri' },
  { value: 'menopause',       labelKey: 'personalize_stage_meno' },
  { value: 'postmenopause',   labelKey: 'personalize_stage_post' },
  { value: 'unspecified',     labelKey: 'personalize_stage_unsure' },
];

const CONCERNS = [
  { value: 'leakage',         labelKey: 'personalize_concern_leakage' },
  { value: 'painful_sex',     labelKey: 'personalize_concern_painful_sex' },
  { value: 'pelvic_pain',     labelKey: 'personalize_concern_pelvic_pain' },
  { value: 'urgency',         labelKey: 'personalize_concern_urgency' },
  { value: 'postpartum',      labelKey: 'personalize_concern_postpartum' },
  { value: 'cycle_regularity',labelKey: 'personalize_concern_cycle_regularity' },
  { value: 'pms',             labelKey: 'personalize_concern_pms' },
  { value: 'hot_flushes',     labelKey: 'personalize_concern_hot_flushes' },
  { value: 'low_libido',      labelKey: 'personalize_concern_low_libido' },
  { value: 'sleep',           labelKey: 'personalize_concern_sleep' },
  { value: 'noticing',        labelKey: 'personalize_concern_noticing' },
];

const MAX_CONCERNS = 3;

const s = {
  page: {
    position: 'fixed', inset: 0, zIndex: 9998,
    background: 'var(--cream, #FAF6F1)',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    overflowY: 'auto',
    padding: 'max(28px, env(safe-area-inset-top)) 20px max(28px, env(safe-area-inset-bottom))',
  },
  inner: {
    width: '100%', maxWidth: 460,
    display: 'flex', flexDirection: 'column', gap: 18,
  },
  eyebrow: {
    fontFamily: 'var(--font-ui)',
    fontSize: 11, fontWeight: 700,
    letterSpacing: '0.22em', textTransform: 'uppercase',
    color: 'var(--warm-brown, #8B7355)',
    margin: '0 0 4px',
    textAlign: 'center',
  },
  title: {
    fontFamily: 'var(--font-title, var(--font-display))',
    fontSize: 26, fontWeight: 600,
    color: 'var(--ink, #2A2520)',
    margin: '0 0 4px',
    textAlign: 'center',
    lineHeight: 1.25,
  },
  sub: {
    fontFamily: 'var(--font-ui)',
    fontSize: 14,
    color: 'var(--warm-brown)',
    lineHeight: 1.55,
    margin: '0 0 18px',
    textAlign: 'center',
  },
  field: {
    background: 'var(--surface, #FFFFFF)',
    border: '1px solid var(--border, #E8DDD4)',
    borderRadius: 16,
    padding: '16px 18px',
  },
  fieldLabel: {
    fontFamily: 'var(--font-ui)',
    fontSize: 11, fontWeight: 700,
    letterSpacing: '0.18em', textTransform: 'uppercase',
    color: 'var(--warm-brown)',
    margin: '0 0 10px',
  },
  fieldHint: {
    fontFamily: 'var(--font-ui)',
    fontSize: 12, color: 'var(--warm-brown)',
    lineHeight: 1.5,
    margin: '8px 0 0',
  },
  stageGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  },
  stageChip: {
    minHeight: 44,
    padding: '8px 10px',
    borderRadius: 12,
    border: '1px solid var(--border, #E8DDD4)',
    background: 'var(--surface, #FFFFFF)',
    fontFamily: 'var(--font-ui)',
    fontSize: 13, fontWeight: 600,
    color: 'var(--dark-brown, #5A3E2B)',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    textAlign: 'left',
  },
  stageChipActive: {
    background: 'var(--primary-fixed, #FCE4EC)',
    borderColor: 'var(--primary, #af004f)',
    color: 'var(--primary, #af004f)',
  },
  dateInput: {
    width: '100%', minHeight: 44,
    padding: '10px 12px',
    borderRadius: 12,
    border: '1px solid var(--border, #E8DDD4)',
    background: 'var(--surface, #FFFFFF)',
    fontFamily: 'var(--font-ui)',
    fontSize: 15,
    color: 'var(--ink)',
    boxSizing: 'border-box',
  },
  concernsRow: {
    display: 'flex', flexWrap: 'wrap', gap: 8,
  },
  concernChip: {
    minHeight: 36, padding: '6px 12px',
    borderRadius: 999,
    border: '1px solid var(--border, #E8DDD4)',
    background: 'var(--surface, #FFFFFF)',
    fontFamily: 'var(--font-ui)',
    fontSize: 12, fontWeight: 600,
    color: 'var(--dark-brown, #5A3E2B)',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  concernChipActive: {
    background: 'var(--primary-fixed, #FCE4EC)',
    borderColor: 'var(--primary, #af004f)',
    color: 'var(--primary, #af004f)',
  },
  concernChipDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  ctaRow: {
    display: 'flex', gap: 10, marginTop: 10,
  },
  primary: {
    flex: 1, minHeight: 50,
    padding: '12px 18px', borderRadius: 999,
    background: 'var(--primary, #af004f)',
    color: '#FFF', border: 'none',
    fontFamily: 'var(--font-ui)',
    fontSize: 13, fontWeight: 700,
    letterSpacing: '0.12em', textTransform: 'uppercase',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  skip: {
    flex: 1, minHeight: 50,
    padding: '12px 18px', borderRadius: 999,
    background: 'transparent',
    color: 'var(--warm-brown)',
    border: '1px solid var(--border, #E8DDD4)',
    fontFamily: 'var(--font-ui)',
    fontSize: 12, fontWeight: 600,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
};

function todayIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export default function PersonalizeStep({ onComplete }) {
  const { user, userProgress, patchProgress } = useApp();
  const t = useStrings();
  const [stage, setStage] = useState(userProgress?.life_stage || null);
  const [periodDate, setPeriodDate] = useState(userProgress?.cycle_last_period_start || '');
  const [concerns, setConcerns] = useState(
    Array.isArray(userProgress?.concerns) ? userProgress.concerns : [],
  );
  const [saving, setSaving] = useState(false);

  // Cycle history capture only makes sense for life stages where
  // someone is still bleeding. Peri / meno users get the question
  // anyway because peri cycles still happen, just unpredictably; we
  // soft-hide it once they pick `menopause` to reduce noise.
  const showPeriodAsk = stage !== 'menopause' && stage !== 'postmenopause';

  const toggleConcern = useCallback((value) => {
    setConcerns((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (prev.length >= MAX_CONCERNS) return prev;
      return [...prev, value];
    });
  }, []);

  const persistAndAdvance = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    const patch = {};
    if (stage) patch.life_stage = stage;
    // Validate the date input shape before storing; native <input
    // type="date"> usually emits YYYY-MM-DD but a paste / buggy
    // browser can slip through.
    const validDate = /^\d{4}-\d{2}-\d{2}$/.test(periodDate)
      && periodDate <= todayIso();
    if (validDate) patch.cycle_last_period_start = periodDate;
    if (concerns.length > 0) patch.concerns = concerns;

    if (Object.keys(patch).length > 0) {
      if (typeof patchProgress === 'function') patchProgress(patch);
      vibrateTap();
      // Aggregated signal only: which stage they picked + how many
      // concerns. No specific date, no full concerns list.
      track('onboarding_personalize_saved', {
        stage: stage || 'unanswered',
        has_period_date: validDate,
        concerns_count: concerns.length,
      });
      // Fire-and-forget: never block the next onboarding screen on a
      // Supabase write. patchProgress already updated the in-memory
      // state so downstream screens see the values immediately.
      safeWrite(user?.id, patch).catch(() => {});
    } else {
      track('onboarding_personalize_skipped');
    }
    setSaving(false);
    onComplete?.();
  }, [saving, stage, periodDate, concerns, patchProgress, user, onComplete]);

  const onSkipAll = useCallback(() => {
    track('onboarding_personalize_skipped');
    onComplete?.();
  }, [onComplete]);

  const todayCutoff = todayIso();
  const concernsAtMax = concerns.length >= MAX_CONCERNS;

  return (
    <div style={s.page}>
      <div style={s.inner}>
        <p style={s.eyebrow}>{t('personalize_eyebrow')}</p>
        <h1 style={s.title}>{t('personalize_title')}</h1>
        <p style={s.sub}>{t('personalize_sub')}</p>

        <section style={s.field} aria-labelledby="personalize-stage-label">
          <p id="personalize-stage-label" style={s.fieldLabel}>
            {t('personalize_stage_label')}
          </p>
          <div style={s.stageGrid} role="radiogroup" aria-labelledby="personalize-stage-label">
            {LIFE_STAGES.map(({ value, labelKey }) => {
              const active = stage === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setStage(value)}
                  style={{ ...s.stageChip, ...(active ? s.stageChipActive : null) }}
                >
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
        </section>

        {showPeriodAsk ? (
          <section style={s.field}>
            <p style={s.fieldLabel}>{t('personalize_period_label')}</p>
            <input
              type="date"
              value={periodDate}
              max={todayCutoff}
              onChange={(e) => setPeriodDate(e.target.value)}
              style={s.dateInput}
              aria-label={t('personalize_period_aria')}
            />
            <p style={s.fieldHint}>{t('personalize_period_hint')}</p>
          </section>
        ) : null}

        <section style={s.field} aria-labelledby="personalize-concerns-label">
          <p id="personalize-concerns-label" style={s.fieldLabel}>
            {t('personalize_concerns_label')}
          </p>
          <div style={s.concernsRow} role="group" aria-labelledby="personalize-concerns-label">
            {CONCERNS.map(({ value, labelKey }) => {
              const active = concerns.includes(value);
              const disabled = !active && concernsAtMax;
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={active}
                  disabled={disabled}
                  onClick={() => toggleConcern(value)}
                  style={{
                    ...s.concernChip,
                    ...(active ? s.concernChipActive : null),
                    ...(disabled ? s.concernChipDisabled : null),
                  }}
                >
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
          <p style={s.fieldHint}>
            {`${concerns.length} / ${MAX_CONCERNS} ${t('personalize_concerns_hint')}`}
          </p>
        </section>

        <div style={s.ctaRow}>
          <button type="button" style={s.skip} onClick={onSkipAll}>
            {t('personalize_skip')}
          </button>
          <button type="button" style={s.primary} onClick={persistAndAdvance} disabled={saving}>
            {t('personalize_continue')}
          </button>
        </div>
      </div>
    </div>
  );
}
